
import Vue from "vue";
import { Component, Provide } from "vue-property-decorator";
import lodash from "lodash";
import {
  configStore,
  documentStore,
  globalStore,
  uiStore
} from "@/store/store";
import { ChangeControlValueMeta, ControlsTreeContext } from "@/types/controls";
import ControlsTree from "@/components/Reusable/ControlsTree/ControlsTree.vue";
import SearchBox, {
  SearchBoxState
} from "@/components/ElementPropertiesPanel/SearchBox.vue";
import defensiveCopy from "@/util/defensive-copy";
import IconButton from "@/components/UI/IconButton.vue";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import {
  getPresetAccordionState,
  setPresetAccordionState
} from "@/lib/panels/accordions";
import { addMetadataToPresetPropertyChangeBasingOnControlValueChangeMetadata } from "@/lib/controls/utils";
import { filterControls, flattenAndCount } from "@/lib/controls/search";
import SearchBoxEmptyState from "@/components/ElementPropertiesPanel/SearchBoxEmptyState.vue";
import Icon from "@/components/UI/Icon.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import FreeModeAlertForPresets from "@/components/Subscription/FreeModeAlertForPresets.vue";
import ElementIcon from "@/components/UI/ElementIcon.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { removeContentSectionConditions } from "@/lib/presets/presets";
import { getNodesByPresetId } from "@/lib/tree/tree";
import { breakdanceStoreActions } from "@/store/actions/store-actions";

@Component({
  components: {
    Icon,
    ElementIcon,
    FreeModeAlertForPresets,
    AlertBox,
    ControlsTree,
    SearchBox,
    IconButton,
    PanelHeader,
    TextInput,
    SearchBoxEmptyState,
    NiceButton
  }
})
export default class EditPreset extends Vue {
  searchBoxState: SearchBoxState = {
    modifiedOnly: false,
    query: ""
  };

  updateSearchBoxState(newSearchBoxState: SearchBoxState) {
    this.searchBoxState = newSearchBoxState;
  }

  clearQuery() {
    this.updateSearchBoxState({
      modifiedOnly: false,
      query: ""
    });
  }

  skipTypeCheckForActiveElement = false;

  get isActiveElementRelatedToActivePreset() {
    return (
      this.activeElement &&
      this.activeElement.data.type === this.activePreset?.availableForType &&
      this.activeElement.data.properties.meta.preset === this.activePreset?.id
    );
  }

  get activeElement() {
    return uiStore.activeElement;
  }

  get activeElementId() {
    return uiStore.activeElementId;
  }

  get hasSearchParameters() {
    return this.searchBoxState.modifiedOnly || this.searchBoxState.query;
  }

  get controlsFilteredBySearchParams() {
    return filterControls(
      this.controls,
      this.searchBoxState.modifiedOnly,
      this.searchBoxState.query,
      this.editingPresetControlsTreeContext,
      "design"
    );
  }

  get noSearchResults() {
    return flattenAndCount(this.controlsFilteredBySearchParams, 0) === 0;
  }

  get element() {
    if (!this.activePreset) return null;
    return configStore.element(this.activePreset.availableForType);
  }

  get controls() {
    const controls = this.element?.controls.designSections ?? [];
    return removeContentSectionConditions(controls);
  }

  get activePreset() {
    return uiStore.activePreset;
  }

  deletePreset() {
    if (!this.activePreset) return;

    const preset = this.activePreset;
    const indexToDelete = globalStore.presets.findIndex(
      s => s.id === preset.id
    );

    if (indexToDelete !== -1) {
      const newPresets = defensiveCopy(globalStore.presets);
      newPresets.splice(indexToDelete, 1);
      globalStore.setPresets(newPresets);
    }
  }

  close() {
    this.backToAllPresets();
  }

  backToAllPresets() {
    uiStore.setActivePresetId(null);
  }

  attemptingToRename: string | false = false;

  tryToRenameActivePreset(newName: string | null) {
    if (!newName) return;

    this.attemptingToRename = newName;

    const activePresetIndex = globalStore.presets.findIndex(
      p => p.id === this.activePreset?.id
    );

    const newPresets = defensiveCopy(globalStore.presets);

    if (newPresets[activePresetIndex]) {
      newPresets[activePresetIndex].name = newName;
      globalStore.setPresets(newPresets);
      this.attemptingToRename = false;
    }
  }

  determineArticle(word?: string) {
    if (!word) return "a";

    // Converting the first letter to lower case for comparison
    const firstLetter = word[0].toLowerCase();

    // Checking if the first letter is a vowel
    if (["a", "e", "i", "o", "u"].includes(firstLetter)) {
      return "an";
    } else {
      return "a";
    }
  }

  @Provide("context")
  editingPresetControlsTreeContext: ControlsTreeContext = {
    dynamicProperty: () => false,
    getControls: () => this.controls,
    getControlValue: controlPath => {
      return lodash.get(uiStore.activePreset, `${controlPath}`, undefined);
    },
    setControlValue(path, newValue, control, meta?: ChangeControlValueMeta) {
      if (!uiStore.activePreset) return;

      const nodes = getNodesByPresetId(
        documentStore.document.tree,
        uiStore.activePreset.id
      );

      globalStore.throttledPresetPropertyChange(
        addMetadataToPresetPropertyChangeBasingOnControlValueChangeMetadata(
          {
            preset: uiStore.activePreset,
            path,
            value: newValue
          },
          {
            control,
            controlsTree: this.getControls(path) || []
          },
          meta
        )
      );

      // Trigger onPropertyChange event on all nodes that have this preset applied
      nodes.forEach(node => {
        breakdanceStoreActions.run.onPropertyChange({
          elementId: node.id,
          path,
          value: defensiveCopy(newValue),
          previousValue: "",
          transactionKey: undefined
        });
      });
    },
    setAccordionState: (accordionPath: string, open: boolean) => {
      const activePresetId = uiStore.activePreset && uiStore.activePreset.id;
      if (!activePresetId) return;
      setPresetAccordionState(activePresetId, accordionPath, open);
    },
    getAccordionState: (accordionPath: string) => {
      const activePresetId = uiStore.activePreset && uiStore.activePreset.id;
      if (!activePresetId) return false;
      return getPresetAccordionState(activePresetId, accordionPath);
    }
  };
}
